import { useCallback, useEffect, useState } from 'react';
import { Button, Input, message, Modal, Select, Spin } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAiWorkspace } from '@/hooks/useAiWorkspace';
import { WorkspaceSidebar, type SidebarSection } from '@/components/ai-workspace/WorkspaceSidebar';
import { AIWorkspaceHeader } from '@/components/ai-workspace/AIWorkspaceHeader';
import { ContextDrawer } from '@/components/ai-workspace/ContextDrawer';
import { AIChatThread } from '@/components/ai-workspace/AIChatThread';
import { AIWorkspaceSearch } from '@/components/ai-workspace/AIWorkspaceSearch';
import { AIWorkspaceHome } from '@/pages/ai/AIWorkspaceHome';
import { AgentInput } from '@/components/ai-agent/AgentInput';
import { ActionConfirmModal } from '@/components/ai-agent/ActionConfirmModal';
import { AIFollowUpChip } from '@/components/ai-design-system';
import type { ActionPayload } from '@/components/ai-agent/action-payload';
import { findLastExportableTable } from '@/components/ai-workspace/workspaceExport.util';
import {
  createPin,
  createWorkspaceShare,
  deleteConversation,
  duplicateConversation,
  fetchPins,
  fetchSavedReports,
  fetchWorkspaceShares,
  patchConversation,
  downloadAssistantTablePdf,
} from '@/services/aiAgent.service';
import { fetchAiAgentConfig } from '@/services/aiAgentConfig.service';
import { userService } from '@/services/user.service';

const SHORT_THREAD_CHIPS = ['What changed since yesterday?', 'Show top risks', 'Summarize key metrics'];

export default function AiWorkspacePage() {
  const ws = useAiWorkspace();
  const navigate = useNavigate();
  const [contextOpen, setContextOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarSection, setSidebarSection] = useState<SidebarSection>('workspace');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pins, setPins] = useState<Awaited<ReturnType<typeof fetchPins>>>([]);
  const [reports, setReports] = useState<Awaited<ReturnType<typeof fetchSavedReports>>>([]);
  const [shares, setShares] = useState<Awaited<ReturnType<typeof fetchWorkspaceShares>>>([]);
  const [assistantEnabled, setAssistantEnabled] = useState(true);
  const [input, setInput] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<ActionPayload | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUserId, setShareUserId] = useState('');

  const showHome = ws.messages.length === 0 && !ws.loading && !ws.conversationLoading;

  useEffect(() => {
    void (async () => {
      try {
        const cfg = await fetchAiAgentConfig();
        setAssistantEnabled(cfg.config?.enabled !== false);
      } catch {
        setAssistantEnabled(true);
      }
    })();
  }, []);

  const refreshMeta = useCallback(async () => {
    try {
      const [p, r, s] = await Promise.all([fetchPins(), fetchSavedReports(), fetchWorkspaceShares('conversation')]);
      setPins(p);
      setReports(r);
      setShares(s);
    } catch {
      setPins([]);
    }
  }, []);

  useEffect(() => {
    void refreshMeta();
  }, [refreshMeta, ws.messages.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const usersQ = useQuery({
    queryKey: ['workspace-share-users'],
    queryFn: () => userService.getAll({ is_active: true }),
    enabled: shareOpen,
    staleTime: 120_000,
  });

  const handleModeChange = useCallback(
    async (mode: typeof ws.mode) => {
      ws.setMode(mode);
      if (ws.activeConversation?.id) await ws.updateConversationMeta({ mode });
    },
    [ws]
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || ws.loading || !assistantEnabled) return;
    setInput('');
    await ws.sendMessage(text, false);
  }, [assistantEnabled, input, ws]);

  const handleShare = async () => {
    if (!ws.activeConversation?.id || !shareUserId.trim()) return;
    await createWorkspaceShare({
      resourceType: 'conversation',
      resourceId: ws.activeConversation.id,
      sharedWithUserId: shareUserId.trim(),
      permission: 'view',
    });
    message.success('Conversation shared');
    setShareOpen(false);
    setShareUserId('');
  };

  const sharedConversations = shares
    .filter((s) => s.resourceType === 'conversation')
    .map((s) => ({ resourceId: s.resourceId, title: 'Shared conversation' }));

  return (
    <div className="ai-workspace-body">
      <div className={`ai-workspace-sidebar-wrap${mobileNavOpen ? ' mobile-open' : ''}`}>
        <WorkspaceSidebar
          section={sidebarSection}
          onSectionChange={setSidebarSection}
          conversations={ws.conversations}
          sharedConversations={sharedConversations}
          reports={reports}
          activeId={ws.activeConversation?.id}
          loading={ws.listLoading}
          searchQuery={ws.searchQuery}
          onSearchChange={ws.setSearchQuery}
          onNewChat={() => void ws.startNewChat()}
          onSelectConversation={(id) => {
            void ws.loadConversation(id);
            setMobileNavOpen(false);
          }}
          onSelectReport={(id) => navigate(`/ai/udq/${id}`)}
          pinCount={pins.length}
          onRename={(id, title) => {
            setRenameValue(title);
            setRenameOpen(true);
            if (ws.activeConversation?.id !== id) void ws.loadConversation(id);
          }}
          onPin={(id, pinned) => void patchConversation(id, { pinned }).then(() => ws.refreshList())}
          onFavorite={(id, favorite) => void patchConversation(id, { favorite }).then(() => ws.refreshList())}
          onArchive={(id) => void patchConversation(id, { archived: true }).then(() => ws.refreshList())}
          onDuplicate={(id) =>
            void duplicateConversation(id).then((c) => {
              message.success('Duplicated');
              void ws.loadConversation(c.id);
              void ws.refreshList();
            })
          }
          onDelete={(id) =>
            Modal.confirm({
              title: 'Delete conversation?',
              onOk: () => void deleteConversation(id).then(() => ws.refreshList()),
            })
          }
          onShare={() => setShareOpen(true)}
        />
      </div>
      <div className="ai-workspace-main">
        <AIWorkspaceHeader
          title={ws.activeConversation?.title}
          mode={ws.mode}
          onModeChange={(m) => void handleModeChange(m)}
          onTitleChange={(t) => {
            if (ws.activeConversation?.id) void patchConversation(ws.activeConversation.id, { title: t });
          }}
          onOpenContext={() => setContextOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          onShare={() => setShareOpen(true)}
          onExport={() => {
            const body = findLastExportableTable(ws.messages);
            if (!body) {
              message.info('No table to export in this conversation');
              return;
            }
            void downloadAssistantTablePdf(body).catch(() => message.error('Export failed'));
          }}
          pinCount={pins.length}
          onOpenDashboard={() => navigate('/ai/dashboard')}
          mobileMenu={
            <Button type="text" icon={<MenuOutlined />} className="mobile-only" onClick={() => setMobileNavOpen((o) => !o)} aria-label="Toggle menu" />
          }
        />
        <div className="ai-workspace-chat-scroll">
          {showHome ? (
            <AIWorkspaceHome mode={ws.mode} onPrompt={(p) => void ws.sendMessage(p, false)} />
          ) : (
            <>
              {ws.conversationLoading && ws.messages.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                  <Spin />
                </div>
              ) : (
                <AIChatThread
                  messages={ws.messages}
                  onActionPrompt={(p) => void ws.sendMessage(p, false)}
                  onPin={(input) =>
                    void createPin(input).then(() => {
                      message.success('Pinned — view on Dashboard');
                      void refreshMeta();
                    })
                  }
                  onSaveUdq={(input) => {
                    const params = new URLSearchParams();
                    params.set('intent', input.intent);
                    params.set('new', '1');
                    if (input.title) params.set('q', input.title);
                    if (input.filters) params.set('filters', JSON.stringify(input.filters));
                    navigate(`/ai/udq?${params.toString()}`);
                  }}
                />
              )}
            </>
          )}
        </div>
        <div style={{ padding: '12px 32px 20px', borderTop: '1px solid var(--ai-border)', background: 'var(--ai-surface)' }}>
          <div className="ai-workspace-chat-inner">
            {ws.messages.length > 0 && ws.messages.length <= 4 ? (
              <div className="ai-follow-up-row" style={{ marginBottom: 10 }}>
                {SHORT_THREAD_CHIPS.map((chip) => (
                  <AIFollowUpChip key={chip} label={chip} onClick={() => void ws.sendMessage(chip, false)} />
                ))}
              </div>
            ) : null}
            <AgentInput value={input} onChange={setInput} onSend={() => void send()} disabled={ws.loading || ws.conversationLoading || !assistantEnabled} />
          </div>
        </div>
      </div>
      <ContextDrawer
        open={contextOpen}
        onClose={() => setContextOpen(false)}
        pageContext={ws.pageContext}
        mode={ws.mode}
        onModeChange={(m) => void handleModeChange(m)}
        contextFilters={ws.contextFilters}
        onContextFiltersChange={ws.setContextFilters}
      />
      <AIWorkspaceSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ActionConfirmModal
        open={confirmOpen}
        payload={confirmPayload}
        onConfirm={() => {
          setConfirmOpen(false);
          void ws.sendMessage(ws.lastUserMessageRef.current, true);
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmPayload(null);
        }}
      />
      <Modal title="Rename conversation" open={renameOpen} onOk={() => {
        if (ws.activeConversation?.id && renameValue.trim()) {
          void patchConversation(ws.activeConversation.id, { title: renameValue.trim() }).then(() => {
            setRenameOpen(false);
            void ws.refreshList();
          });
        }
      }} onCancel={() => setRenameOpen(false)}>
        <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
      </Modal>
      <Modal title="Share conversation" open={shareOpen} onOk={() => void handleShare()} onCancel={() => setShareOpen(false)}>
        <Select
          showSearch
          placeholder="Select a user to share with"
          style={{ width: '100%' }}
          value={shareUserId || undefined}
          onChange={setShareUserId}
          optionFilterProp="label"
          loading={usersQ.isLoading}
          options={(usersQ.data || []).map((u) => ({
            value: u.id,
            label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email || u.id,
          }))}
          aria-label="Share with user"
        />
      </Modal>
      {mobileNavOpen ? (
        <button
          type="button"
          className="ai-sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}
    </div>
  );
}
